import { useCallback } from "react";
import type { Person } from "@b2bportal/air-booking-api";
import {
  IconNameEnum,
  useCheckoutStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreCheckoutComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import defaultStyles from "./PassengerReview.module.scss";

const PassengerInfo = ({
  name,
  passengerType,
  onEdit,
}: {
  name: string;
  passengerType: string;
  onEdit?: () => void;
}) => {
  const COMPONENT_KEY = CoreCheckoutComponent.PassengerReview;
  const styles = useCheckoutStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  return (
    <div className={cn("info-container")}>
      <div className={cn("name")}>{name}</div>
      <div className={cn("type")}>{passengerType}</div>
      {onEdit != null ? (
        <button className={cn("edit-button")} onClick={onEdit}>
          <Icon iconName={IconNameEnum.edit} /> <span>{t("edit")}</span>
        </button>
      ) : null}
    </div>
  );
};

export interface PassengerReviewComponentProps extends PlatformComponentProps {
  passengers: Person[];
  onPassengerEdit?: (passenger: Person) => void;
}
export const PassengerReview = ({
  className,
  passengers,
  onPassengerEdit,
  children,
}: PassengerReviewComponentProps) => {
  const COMPONENT_KEY = CoreCheckoutComponent.PassengerReview;
  const styles = useCheckoutStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const getPassengerLabel = useCallback(
    (passenger: Person) => {
      const age = dayjs().diff(
        dayjs(passenger.dateOfBirth, "YYYY-MM-DD"),
        "year"
      );
      if (age > 11) {
        return t("passengerInformation.adult");
      } else if (age >= 2) {
        return t("passengerInformation.child");
      } else {
        return t("passengerInformation.infant");
      }
    },
    [t]
  );

  return (
    <div className={clsx(block, className)}>
      <div className={cn("header-container")}>
        <div className={cn("title")}>{t("travelerForm.title")}</div>
        <div className={cn("subtitle")}>{t("travelerForm.subtitle")}</div>
      </div>

      {passengers.map((passenger) => {
        return (
          <PassengerInfo
            key={passenger.id}
            name={`${passenger?.givenName} ${passenger?.surname}`}
            passengerType={getPassengerLabel(passenger)}
            onEdit={
              onPassengerEdit != null
                ? () => onPassengerEdit(passenger)
                : undefined
            }
          />
        );
      })}
      {children}
    </div>
  );
};
